import React from 'react'
import PropTypes from 'prop-types'

import moment from 'moment'
import _ from 'lodash'
import CircularProgress from 'material-ui/CircularProgress'
import Popover from 'material-ui/Popover'
import Menu from 'material-ui/Menu'
import MenuItem from 'material-ui/MenuItem'

import BillingItemDeletionConfirmationDialog from 'billing/common/modules/items/BillingItemDeletionConfirmationDialog'
import InvoicesRepository from '../infra/InvoicesRepository'
import Utils from '../infra/Utils'

const INVOICE_ACTION_VIEW = 'V'
const INVOICE_ACTION_EDIT = 'E'
const INVOICE_ACTION_DELETE = 'D'
const INVOICE_ACTION_EXPORT_AS_PDF = 'P'
const INVOICE_ACTION_SEND = 'S'
const INVOICE_ACTION_RECORD_PAYMENT = 'R'
const INVOICE_ACTION_DUPLICATE = 'U'

const INVOICE_ACTIONS = [
  { key: INVOICE_ACTION_VIEW, display: () => 'View' },
  { key: INVOICE_ACTION_EDIT, display: () => 'Edit' },
  { key: INVOICE_ACTION_RECORD_PAYMENT, display: () => 'Record Payment' },
  { key: INVOICE_ACTION_DUPLICATE, display: () => 'Duplicate' },
  { key: INVOICE_ACTION_SEND, display: status => status === 'draft' ? 'Send' : 'Resend' },
  { key: INVOICE_ACTION_EXPORT_AS_PDF, display: () => 'Export as PDF' },
  { key: INVOICE_ACTION_DELETE, display: () => 'Delete' }
]

const InvoicesListHeader = () => {
  return (
    <div style={styles.listItemContainer}>
      <div style={styles.listColumn(true, 32, true, 'right')}>#</div>
      <div style={styles.listColumn(true)}>Status</div>
      <div style={styles.listColumn(true, 192)}>Customer</div>
      <div style={styles.listColumn(true)}>Date</div>
      <div style={styles.listColumn(true, null, false, 'right')}>Total</div>
      <div style={styles.listColumn(true, null, false, 'right')}>Amount Due</div>
      <div style={styles.listColumn(true, null, false, 'center')}>Actions</div>
    </div>
  )
}

const handleActionsClicked = (event, invoice, onInvoiceActionsClicked) => {
  event.preventDefault()
  onInvoiceActionsClicked(event.currentTarget, invoice)
}

const InvoicesListItem = ({ index, invoice, onInvoiceActionsClicked, onInvoiceViewClicked }) => {

  const invoiceDateStr = moment(invoice.invoiceDate).format('MMM Do, YYYY')
  const amountDue = Utils.getBillAmountDue(invoice)

  return (
    <div style={{ ...styles.listItemContainer, ...styles.row(index) }}>
      <div style={styles.listColumn(false, 32, true, 'right')}>{ invoice.number }</div>
      <div style={styles.listColumn()}>
        <div style={styles.statusBox(invoice.status)}>
          { invoice.status && invoice.status.toUpperCase() }
        </div>
      </div>
      <div style={styles.listColumn(false, 192)}>{ invoice.customerName }</div>
      <div style={styles.listColumn()}>{ invoiceDateStr }</div>
      <div style={styles.listColumn(false, null, false, 'right')}>${ invoice.total && invoice.total.toFixed(2) || 0}</div>
      <div style={styles.listColumn(false, null, false, 'right')}>${ amountDue.toFixed(2) }</div>
      <div style={styles.listColumn(false, null, false, 'center')}>
        <div style={styles.actionsContainer}>
          <div style={styles.viewLabel} onClick={() => onInvoiceViewClicked(invoice)}>View</div>
          <div style={styles.actionsBox} onClick={e => handleActionsClicked(e, invoice, onInvoiceActionsClicked)}>
            <i className='fa fa-caret-down'></i>
          </div>
        </div>
      </div>
    </div>
  )

}

InvoicesListItem.propTypes = {
  index: PropTypes.number.isRequired,
  invoice: PropTypes.object.isRequired,
  onInvoiceViewClicked: PropTypes.func.isRequired,
  onInvoiceActionsClicked: PropTypes.func.isRequired
}

export default class InvoicesList extends React.Component {

  static propTypes = {
    style: PropTypes.object,
    filters: PropTypes.object.isRequired,
    teamId: PropTypes.string.isRequired,
    onInvoiceViewRequested: PropTypes.func.isRequired,
    onInvoiceSendSelected: PropTypes.func.isRequired,
    onInvoiceEditRequested: PropTypes.func.isRequired,
    onInvoiceDuplicateRequested: PropTypes.func.isRequired,
    onInvoiceRecordPaymentRequested: PropTypes.func.isRequired,
    onInvoiceDownloadRequested: PropTypes.func.isRequired
  }

  constructor(props) {
    super(props)
    this.state = {
      loading: true,
      showInvoiceOptionsPopover: false,
      showInvoiceDeletionConfirmationDialog: false,
      showRecordPaymentDialog: false
    }
  }

  componentDidMount() {
    this.attachInvoicesListListener(this.props)
  }

  componentWillUnmount() {
    this.removeInvoicesListListener(this.props)
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.filters !== this.props.filters) {
      this.setState({
        loading: true
      })
      this.removeInvoicesListListener(this.props)
      this.attachInvoicesListListener(nextProps)
    }
  }

  attachInvoicesListListener = props => {
    InvoicesRepository.addInvoicesListListener(props.teamId,
      props.filters, this.onInvoicesListValue)
  }

  removeInvoicesListListener = props => {
    InvoicesRepository.removeInvoicesListListener(props.teamId,
      props.filters, this.onInvoicesListValue)
  }

  onInvoicesListValue = snapshot => {
    const value = snapshot && snapshot.val() || {}
    this.setState({
      loading: false,
      list: _.map(value)
    })
  }

  handleInvoiceActionsClicked = (anchorElement, selectedInvoice) => {
    this.setState({
      showInvoiceOptionsPopover: true,
      anchorElement,
      selectedInvoice
    })
  }

  handleInvoiceOptionsCloseRequested = () => {
    this.setState({
      showInvoiceOptionsPopover: false
    })
  }

  handleInvoiceViewSelected = invoice => {
    this.props.onInvoiceViewRequested(invoice)
    this.setState({
      showInvoiceOptionsPopover: false
    })
  }

  handleInvoiceEditSelected = invoice => {
    this.props.onInvoiceEditRequested(invoice)
    this.setState({
      showInvoiceOptionsPopover: false
    })
  }

  handleInvoiceDuplicateSelected = invoice => {
    this.props.onInvoiceDuplicateRequested(invoice)
    this.setState({
      showInvoiceOptionsPopover: false
    })
  }

  handleInvoiceDeleteSelected = () => {
    this.setState({
      showInvoiceOptionsPopover: false,
      showInvoiceDeletionConfirmationDialog: true
    })
  }

  handleInvoiceExportAsPDFSelected = invoice => {
    this.setState({
      showInvoiceOptionsPopover: false
    })
    this.props.onInvoiceDownloadRequested(invoice)
  }

  handleInvoiceSendActionSelected = invoice => {
    this.setState({
      showInvoiceOptionsPopover: false
    })
    this.props.onInvoiceSendSelected(invoice)
  }

  handleInvoiceRecordPaymentSelected = invoice => {
    this.setState({
      showInvoiceOptionsPopover: false
    })
    this.props.onInvoiceRecordPaymentRequested(invoice)
  }

  handleInvoiceActionSelected = (event, actionKey) => {
    event.preventDefault()
    switch (actionKey) {
      case INVOICE_ACTION_VIEW:
        this.handleInvoiceViewSelected(this.state.selectedInvoice)
        break
      case INVOICE_ACTION_EDIT:
        this.handleInvoiceEditSelected(this.state.selectedInvoice)
        break
      case INVOICE_ACTION_DELETE:
        this.handleInvoiceDeleteSelected()
        break
      case INVOICE_ACTION_EXPORT_AS_PDF:
        this.handleInvoiceExportAsPDFSelected(this.state.selectedInvoice)
        break
      case INVOICE_ACTION_SEND:
        this.handleInvoiceSendActionSelected(this.state.selectedInvoice)
        break
      case INVOICE_ACTION_RECORD_PAYMENT:
        this.handleInvoiceRecordPaymentSelected(this.state.selectedInvoice)
        break
      case INVOICE_ACTION_DUPLICATE:
        this.handleInvoiceDuplicateSelected(this.state.selectedInvoice)
        break
    }
  }

  handleInvoiceConfirmationDialogCloseRequested = () => {
    this.setState({
      showInvoiceDeletionConfirmationDialog: false
    })
  }

  handleInvoiceConfirmationDialogResult = result => {
    if (result) {
      InvoicesRepository.deleteInvoice(this.props.teamId, this.state.selectedInvoice)
    }
    this.setState({
      showInvoiceDeletionConfirmationDialog: false
    })
  }

  render() {
    let invoices
    if (this.state.loading) {
      invoices = <div style={styles.progress}>
        <CircularProgress thickness={2} />
      </div>
    }
    else
    if (!this.state.list.length) {
      invoices = <div style={styles.zeroItems}>No invoices have been added or none is matching your filters.</div>
    }
    else {
      invoices = this.state.list.map((invoice, index) =>
        <InvoicesListItem
          key={invoice.id}
          index={index}
          invoice={invoice}
          onInvoiceViewClicked={this.handleInvoiceViewSelected}
          onInvoiceActionsClicked={this.handleInvoiceActionsClicked} />)
    }
    return (
      <div style={this.props.style}>
        <div>
          <InvoicesListHeader />
          { invoices }
        </div>
        <Popover
          open={this.state.showInvoiceOptionsPopover}
          anchorEl={this.state.anchorElement}
          anchorOrigin={{ horizontal: 'middle', vertical: 'bottom' }}
          targetOrigin={{ horizontal: 'left', vertical: 'bottom' }}
          onRequestClose={this.handleInvoiceOptionsCloseRequested}>
          <Menu>
            { INVOICE_ACTIONS.map(action => <MenuItem
              key={action.key}
              onTouchTap={event => this.handleInvoiceActionSelected(event, action.key)}
              primaryText={action.display(this.state.selectedInvoice && this.state.selectedInvoice.status)} />)
            }
          </Menu>
        </Popover>
        <BillingItemDeletionConfirmationDialog
          open={this.state.showInvoiceDeletionConfirmationDialog}
          billingEntity='invoice'
          onCloseRequested={this.handleInvoiceConfirmationDialogCloseRequested}
          onDialogResult={this.handleInvoiceConfirmationDialogResult} />

      </div>
    )
  }

}

const styles = {
  progress: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    margin: 22
  },
  row: index => ({
    backgroundColor: index % 2 ? '#eef7ff' : '#fff',
    borderBottom: 'none'
  }),
  listItemContainer: {
    display:'flex',
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 45,
    justifyContent: 'space-between',
    borderBottom: '1px solid #527da3'
  },
  listColumn: (isHeader, width, isFirstColumn, textAlign) => ({
    color: isHeader ? '#356992' : '#757575',
    fontWeight: isHeader ? 500 : 400,
    letterSpacing: isHeader ? 0.6 : null,
    fontSize: 14,
    width: width || 100,
    marginLeft: isFirstColumn ? 0 : 0,
    textAlign: textAlign || 'left',
    paddingTop: 4,
    paddingBottom: 4
  }),
  listActionsColumn: {
    width: 144,
    justifyContent: 'center'
  },
  actionsContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center'
  },
  viewLabel: {
    cursor: 'pointer',
    color: '#179b93'
  },
  actionsBox: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 8,
    border: 'solid 1px #9b9b9b',
    borderRadius: 3,
    width: 20,
    height: 20,
    cursor: 'pointer'
  },
  statusBox: status => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: 64,
    height: 18,
    fontSize: 10,
    color: '#fff',
    borderRadius: 2,
    border: '1px solid ' + STATUS_BOX_COLOR[status] || '#9b9b9b',
    backgroundColor: STATUS_BOX_COLOR[status] || '#9b9b9b'
  }),
  zeroItems: {
    textAlign: 'center',
    color: 'rgba(0, 0, 0, 0.6)',
    fontSize: 14,
    paddingTop: 12,
    paddingBottom: 12
  }
}

const STATUS_BOX_COLOR = {
  sent: '#efad27',
  paid: '#7ed321',
  partial: '#a49fdb'
}



// WEBPACK FOOTER //
// ./src/billing/invoices/InvoicesList.js